// In-flight coalescing for the chart-of-accounts fetch.
//
// Additive and inert: nothing here runs until the useDocuAi dispatch point in ../main.js calls it,
// so it carries no flag check of its own and survives retirement of the flag. It deliberately holds
// no cache — only requests that are still in flight are joined, so refreshes that must reach the API
// (receipt-form's unmount effect, post-save reloads, the drawer's per-keystroke searchTerm queries)
// are untouched.

type ChartOfAccountsOptions = Record<string, unknown>

const inFlightByKey = new Map<string, Promise<void>>()

/**
 * Identity of a request: the options actually sent, insensitive to their declaration order.
 * Deliberately NOT `JSON.stringify(options, allowlist)`: a replacer array filters keys on EVERY
 * nesting level, so a nested option value would serialise to `{}` — two different requests would
 * share one key and the second would silently adopt the first one's response. Today's options are
 * flat; the key just no longer depends on that staying true.
 */
export const chartsOfAccountsRequestKey = (options: ChartOfAccountsOptions): string =>
  JSON.stringify(Object.keys(options).sort().map(key => [key, options[key]]))

/**
 * Run `fetch` unless an identical request is already in flight, in which case join that one.
 * The returned promise resolves when the underlying request settles.
 */
export const coalesceChartsOfAccounts = (key: string, fetch: () => Promise<void> | void): Promise<void> => {
  const inFlight = inFlightByKey.get(key)
  if (inFlight) { return inFlight }

  const pending = Promise.resolve(fetch())
    .then(() => undefined)
    .finally(() => { inFlightByKey.delete(key) })

  inFlightByKey.set(key, pending)
  return pending
}
